import { useEffect } from "react";

export default function usePreventPullToRefresh() {
  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
    if (!isMobile) return;

    // 优先用 CSS（可选：在全局样式里也添加 overscroll-behavior）
    try {
      document.documentElement.style.overscrollBehavior = "contain";
      document.body.style.touchAction = "manipulation";
    } catch (e) {}

    let startY = 0;
    let maybePrevent = false;
    let touchTarget: EventTarget | null = null;

    function isElement(el: any): el is HTMLElement {
      return !!el && el.nodeType === 1;
    }

    function canScroll(el: Element, deltaY: number) {
      const e = el as HTMLElement;
      const style = window.getComputedStyle(e);
      const overflowY = style.overflowY;
      const isScrollable = overflowY === "auto" || overflowY === "scroll" || e.scrollHeight > e.clientHeight;
      if (!isScrollable) return false;
      if (deltaY > 0) {
        // 向下滚动：只有当 scrollTop > 0 时容器可向下滚
        return e.scrollTop > 0;
      } else if (deltaY < 0) {
        // 向上滚动：只有当未到底部时可向上滚
        return e.scrollTop + e.clientHeight < e.scrollHeight;
      }
      return false;
    }

    function findScrollableAncestor(target: EventTarget | null, deltaY: number) {
      let node: any = target;
      while (node && node !== document.body && node !== document.documentElement && node !== document) {
        if (isElement(node) && canScroll(node, deltaY)) return node;
        node = node.parentNode || node.host;
      }
      if (isElement(document.scrollingElement) && canScroll(document.scrollingElement, deltaY)) return document.scrollingElement;
      return null;
    }

    function onTouchStart(e: TouchEvent) {
      if (e.touches.length !== 1) return;
      startY = e.touches[0].clientY;
      touchTarget = e.target;
      const scrollingEl = document.scrollingElement || document.documentElement;
      const scrollTop = scrollingEl ? (scrollingEl as HTMLElement).scrollTop : window.scrollY || 0;
      maybePrevent = scrollTop === 0;
    }

    function onTouchMove(e: TouchEvent) {
      if (e.touches.length !== 1) return;
      const curY = e.touches[0].clientY;
      const diff = curY - startY;
      // 如果触点在可滚动子容器，且该容器能吸收当前方向滚动，则放行
      const scrollable = findScrollableAncestor(touchTarget, diff);
      if (scrollable) return;

      const scrollingEl = document.scrollingElement || document.documentElement;
      const scrollTop = scrollingEl ? (scrollingEl as HTMLElement).scrollTop : window.scrollY || 0;

      if (maybePrevent && diff > 0 && scrollTop <= 0) {
        if (e.cancelable) {
          try { e.preventDefault(); } catch (err) {}
        }
      }
    }

    window.addEventListener("touchstart", onTouchStart, { passive: false });
    window.addEventListener("touchmove", onTouchMove as EventListener, { passive: false });

    return () => {
      try {
        document.documentElement.style.overscrollBehavior = "";
        document.body.style.touchAction = "";
      } catch (e) {}
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove as EventListener);
    };
  }, []);
}